import React, { useState, useEffect, useRef } from 'react';
import { Tabs, Table, Button, Modal, Form, Input, InputNumber, Select, Space, Popconfirm, Tag, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getSalesTeams, createSalesTeam, updateSalesTeam, deleteSalesTeam } from '../../../services/api';
import { getSalespersons, createSalesperson, updateSalesperson, deleteSalesperson } from '../../../services/api';
import { getProducts, createProduct, updateProduct, deleteProduct } from '../../../services/api';

// ── 团队 Tab ──
function TeamsTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [form] = Form.useForm();

  const fetchData = async (p) => {
    setLoading(true);
    try {
      const res = await getSalesTeams({ page: p, pageSize });
      setData(res.data?.list || []);
      setTotal(res.data?.total || 0);
    } catch (err) { message.error(err.message); }
    setLoading(false);
  };
  useEffect(() => { fetchData(page); }, [page]);

  const refreshToFirst = () => { setPage(1); fetchData(1); };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editRecord) { await updateSalesTeam(editRecord.id, values); }
      else { await createSalesTeam(values); }
      message.success('保存成功');
      setModalOpen(false);
      refreshToFirst();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.message);
    }
  };

  const columns = [
    { title: '团队名称', dataIndex: 'name' },
    { title: '负责人', dataIndex: 'leaderName' },
    { title: '成员数', dataIndex: 'memberCount' },
    { title: '状态', dataIndex: 'status', render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? '启用' : '禁用'}</Tag> },
    { title: '操作', render: (_, r) => (
      <Space>
        <Button size="small" icon={<EditOutlined />} onClick={() => { setEditRecord(r); form.setFieldsValue(r); setModalOpen(true); }}>编辑</Button>
        <Popconfirm title="确定删除？" onConfirm={async () => { await deleteSalesTeam(r.id); refreshToFirst(); }}>
          <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
        </Popconfirm>
      </Space>
    )},
  ];
  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditRecord(null); form.resetFields(); setModalOpen(true); }}>新增团队</Button>
      </Space>
      <Table dataSource={data} columns={columns} rowKey="id" loading={loading}
        pagination={{ current: page, total, pageSize, onChange: setPage, showTotal: (t) => `共 ${t} 条` }} />
      <Modal title={editRecord ? '编辑团队' : '新增团队'} open={modalOpen} onOk={handleSave} onCancel={() => setModalOpen(false)} destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="团队名称" rules={[{ required: true, message: '请输入团队名称' }]}><Input /></Form.Item>
          <Form.Item name="leaderName" label="负责人" rules={[{ required: true, message: '请输入负责人' }]}><Input /></Form.Item>
          <Form.Item name="status" label="状态">
            <Select options={[{ label: '启用', value: 1 }, { label: '禁用', value: 0 }]} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// ── 人员 Tab ──
function PersonsTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [teams, setTeams] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [form] = Form.useForm();

  const fetchData = async (p) => {
    setLoading(true);
    try {
      const [pRes, tRes] = await Promise.all([
        getSalespersons({ page: p, pageSize }),
        getSalesTeams({ pageSize: 200 }),
      ]);
      setData(pRes.data?.list || []);
      setTotal(pRes.data?.total || 0);
      setTeams(tRes.data?.list || []);
    } catch (err) { message.error(err.message); }
    setLoading(false);
  };
  useEffect(() => { fetchData(page); }, [page]);

  const refreshToFirst = () => { setPage(1); fetchData(1); };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editRecord) { await updateSalesperson(editRecord.id, values); }
      else { await createSalesperson(values); }
      message.success('保存成功');
      setModalOpen(false);
      refreshToFirst();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.message);
    }
  };

  const columns = [
    { title: '姓名', dataIndex: 'name' },
    { title: '手机号', dataIndex: 'phone' },
    { title: '所属团队', dataIndex: ['team', 'name'] },
    { title: '状态', dataIndex: 'status', render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? '启用' : '禁用'}</Tag> },
    { title: '操作', render: (_, r) => (
      <Space>
        <Button size="small" icon={<EditOutlined />} onClick={() => { setEditRecord(r); form.setFieldsValue(r); setModalOpen(true); }}>编辑</Button>
        <Popconfirm title="确定删除？" onConfirm={async () => { await deleteSalesperson(r.id); refreshToFirst(); }}>
          <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
        </Popconfirm>
      </Space>
    )},
  ];
  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditRecord(null); form.resetFields(); setModalOpen(true); }}>新增人员</Button>
      </Space>
      <Table dataSource={data} columns={columns} rowKey="id" loading={loading}
        pagination={{ current: page, total, pageSize, onChange: setPage, showTotal: (t) => `共 ${t} 条` }} />
      <Modal title={editRecord ? '编辑人员' : '新增人员'} open={modalOpen} onOk={handleSave} onCancel={() => setModalOpen(false)} destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}><Input /></Form.Item>
          <Form.Item name="phone" label="手机号" rules={[
            { required: true, message: '请输入手机号' },
            { pattern: /^1\d{10}$/, message: '请输入正确的11位手机号' },
          ]}>
            <Input maxLength={11} />
          </Form.Item>
          <Form.Item name="teamId" label="所属团队" rules={[{ required: true, message: '请选择所属团队' }]}>
            <Select options={teams.map(t => ({ label: t.name, value: t.id }))} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select options={[{ label: '启用', value: 1 }, { label: '禁用', value: 0 }]} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// ── 产品 Tab ──
function ProductsTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [form] = Form.useForm();

  const fetchData = async (p) => {
    setLoading(true);
    try {
      const res = await getProducts({ page: p, pageSize });
      setData(res.data?.list || []);
      setTotal(res.data?.total || 0);
    } catch (err) { message.error(err.message); }
    setLoading(false);
  };
  useEffect(() => { fetchData(page); }, [page]);

  const refreshToFirst = () => { setPage(1); fetchData(1); };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (Array.isArray(values.unit)) values.unit = values.unit[0];
      if (editRecord) { await updateProduct(editRecord.id, values); }
      else { await createProduct(values); }
      message.success('保存成功');
      setModalOpen(false);
      refreshToFirst();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.message);
    }
  };

  const columns = [
    { title: '产品名称', dataIndex: 'name' },
    { title: '编码', dataIndex: 'sku' },
    { title: '规格', dataIndex: 'spec' },
    { title: '单位', dataIndex: 'unit' },
    { title: '样品价格', dataIndex: 'samplePrice', render: (v) => `¥${Number(v).toLocaleString()}` },
    { title: '状态', dataIndex: 'status', render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? '启用' : '禁用'}</Tag> },
    { title: '操作', render: (_, r) => (
      <Space>
        <Button size="small" icon={<EditOutlined />} onClick={() => { setEditRecord(r); form.setFieldsValue(r); setModalOpen(true); }}>编辑</Button>
        <Popconfirm title="确定删除？" onConfirm={async () => { await deleteProduct(r.id); refreshToFirst(); }}>
          <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
        </Popconfirm>
      </Space>
    )},
  ];
  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditRecord(null); form.resetFields(); setModalOpen(true); }}>新增产品</Button>
      </Space>
      <Table dataSource={data} columns={columns} rowKey="id" loading={loading}
        pagination={{ current: page, total, pageSize, onChange: setPage, showTotal: (t) => `共 ${t} 条` }} />
      <Modal title={editRecord ? '编辑产品' : '新增产品'} open={modalOpen} onOk={handleSave} onCancel={() => setModalOpen(false)} destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="产品名称" rules={[{ required: true, message: '请输入产品名称' }]}><Input /></Form.Item>
          <Form.Item name="sku" label="编码" rules={[{ required: true, message: '请输入编码' }]}><Input /></Form.Item>
          <Form.Item name="spec" label="规格"><Input /></Form.Item>
          <Form.Item name="unit" label="单位" rules={[{ required: true, message: '请选择或输入单位' }]}>
            <Select mode="tags" maxCount={1}
              options={[
                { label: '盒', value: '盒' },
                { label: '箱', value: '箱' },
                { label: '包', value: '包' },
                { label: '件', value: '件' },
                { label: '条', value: '条' },
                { label: '其他', value: '其他' },
              ]}
              placeholder="选择或输入单位"
            />
          </Form.Item>
          <Form.Item name="samplePrice" label="样品价格(元)" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={0} precision={2} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select options={[{ label: '启用', value: 1 }, { label: '禁用', value: 0 }]} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default function BasicData() {
  return (
    <Tabs items={[
      { key: 'teams', label: '销售团队', children: <TeamsTab /> },
      { key: 'persons', label: '销售人员', children: <PersonsTab /> },
      { key: 'products', label: '样品价格', children: <ProductsTab /> },
    ]} />
  );
}
